/**
 * Video Call Component
 * Uses Daily.co prebuilt iframe for video/audio calls
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { Button } from '@/components/ui/button';
import { PhoneOff, Loader2, RefreshCw } from 'lucide-react';

export function VideoCall({ 
  roomUrl, 
  token, 
  userName,
  callType = 'video',
  onCallEnded
}) {
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const hasJoinedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('initializing');
  const [retryCount, setRetryCount] = useState(0);

  const destroyExisting = async () => {
    // First destroy our ref if it exists
    if (frameRef.current) {
      try {
        console.log('[Call] Destroying frame ref...');
        await frameRef.current.leave().catch(() => {});
        await frameRef.current.destroy().catch(() => {});
      } catch (e) {
        console.log('[Call] Error destroying frame ref:', e.message);
      }
      frameRef.current = null;
    }
    
    // Then try to get and destroy any global instance
    // Loop to ensure all instances are destroyed
    for (let i = 0; i < 3; i++) {
      try {
        const instance = DailyIframe.getCallInstance();
        if (instance) {
          console.log('[Call] Found global instance, destroying...');
          await instance.leave().catch(() => {});
          await instance.destroy().catch(() => {});
          console.log('[Call] Destroyed global instance');
        } else {
          break; // No more instances
        }
      } catch (e) {
        console.log('[Call] No more instances to destroy');
        break;
      }
      await new Promise(r => setTimeout(r, 200));
    }
    
    // Clear any Daily iframes from DOM
    if (containerRef.current) {
      const iframes = containerRef.current.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        try {
          iframe.remove();
        } catch (e) {}
      });
    }
    
    // Wait for cleanup to complete
    await new Promise(r => setTimeout(r, 500));
  };

  const initCall = useCallback(async () => {
    if (!roomUrl || !containerRef.current) return;
    
    setIsLoading(true);
    setError(null);
    setStatus('initializing');
    
    try {
      // Simple cleanup - destroy frameRef if exists
      if (frameRef.current) {
        try {
          console.log('[Call] Cleaning up previous frame ref');
          await frameRef.current.leave().catch(() => {});
          await frameRef.current.destroy().catch(() => {});
        } catch (e) {
          console.log('[Call] Frame ref cleanup error:', e.message);
        }
        frameRef.current = null;
      }
      
      // Clear any Daily iframes from DOM
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      
      // Try to get and destroy any existing global instance
      try {
        const existingInstance = DailyIframe.getCallInstance();
        if (existingInstance) {
          console.log('[Call] Found existing global instance, destroying...');
          await existingInstance.leave().catch(() => {});
          await existingInstance.destroy().catch(() => {});
          // Wait for cleanup
          await new Promise(r => setTimeout(r, 500));
        }
      } catch (e) {
        // No existing instance, that's fine
      }
      
      if (!containerRef.current) return;
      
      setStatus('connecting');
      console.log('[Call] Creating new frame for:', roomUrl);
      
      const frame = DailyIframe.createFrame(containerRef.current, {
        iframeStyle: {
          position: 'absolute',
          top: 0, left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        },
        showLeaveButton: true,
        showFullscreenButton: true,
      });

      frameRef.current = frame;

      frame.on('joined-meeting', () => {
        console.log('[Call] Joined successfully!');
        hasJoinedRef.current = true;
        setIsLoading(false);
        setStatus('connected');
      });

      frame.on('left-meeting', () => {
        console.log('[Call] Left meeting');
        if (hasJoinedRef.current) {
          hasJoinedRef.current = false;
          onCallEnded?.();
        }
      });

      frame.on('error', (e) => {
        console.error('[Call] Error:', e);
        setError(e?.error?.message || 'Call failed');
        setIsLoading(false);
      });

      setStatus('joining room');
      await frame.join({
        url: roomUrl,
        token,
        userName: userName || 'User',
        startVideoOff: callType === 'audio',
        startAudioOff: false,
      });

    } catch (err) {
      console.error('[Call] Error:', err);
      
      if (err.message?.includes('Duplicate')) {
        setError('duplicate');
      } else {
        setError(err.message || 'Failed to connect');
      }
      setIsLoading(false);
    }
  }, [roomUrl, token, userName, callType, onCallEnded]);

  // Initialize on mount or retry
  useEffect(() => {
    initCall();
    
    return () => {
      hasJoinedRef.current = false;
      // Cleanup on unmount
      if (frameRef.current) {
        try {
          frameRef.current.leave().catch(() => {});
          frameRef.current.destroy().catch(() => {});
        } catch (e) {
          console.log('[Call] Cleanup error on unmount:', e.message);
        }
        frameRef.current = null;
      }
      // Also try to destroy any global instance
      try {
        const instance = DailyIframe.getCallInstance();
        if (instance) {
          instance.leave().catch(() => {});
          instance.destroy().catch(() => {});
        }
      } catch (e) {}
    };
  }, [retryCount]); // Re-run when retryCount changes

  const handleRetry = async () => {
    setError(null);
    setIsLoading(true);
    await destroyExisting();
    setRetryCount(c => c + 1);
  };

  const handleEndCall = useCallback(() => {
    if (frameRef.current) {
      hasJoinedRef.current = true;
      try {
        frameRef.current.leave();
      } catch (e) {}
    } else {
      onCallEnded?.();
    }
  }, [onCallEnded]);

  if (error === 'duplicate') {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-900 text-white p-8">
        <div className="text-yellow-400 text-lg mb-4">Connection Issue</div>
        <p className="text-gray-400 mb-6 text-center">A previous call session is still active.</p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleRetry}>
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
          <Button variant="ghost" onClick={() => onCallEnded?.()}>Cancel</Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-900 text-white p-8">
        <div className="text-red-400 text-lg mb-4">Call Error</div>
        <p className="text-gray-400 mb-6 text-center">{error}</p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleRetry}>
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
          <Button variant="ghost" onClick={() => onCallEnded?.()}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      <div ref={containerRef} className="flex-1 relative min-h-[400px]">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
            <div className="text-center text-white">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Connecting...</p>
              <p className="text-xs text-gray-500 mt-2">{status}</p>
            </div>
          </div>
        )}
      </div>
      <div className="p-4 bg-gray-800 border-t border-gray-700 flex justify-center">
        <Button variant="destructive" onClick={handleEndCall}>
          <PhoneOff className="h-4 w-4 mr-2" /> End Call
        </Button>
      </div>
    </div>
  );
}

export const cleanupDailyCall = async () => {
  // Global cleanup function that can be called from anywhere
  try {
    const instance = DailyIframe.getCallInstance();
    if (instance) {
      await instance.leave().catch(() => {});
      await instance.destroy().catch(() => {});
      console.log('[Call] Global cleanup completed');
    }
  } catch (e) {
    console.log('[Call] No instance to clean up globally');
  }
};
export default VideoCall;
